import React from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Col, Container as BootstrapContainer, Row } from 'react-bootstrap';
import {
  JobCandidate,
  useCandidateDetailsQuery,
} from '../../../__generated__/graphql';
import Loading from '../Loading';
import PageHeader from '../../../components/PageHeader';
import Resume from './Resume';
import MyFeedback from './MyFeedback';
import TeamFeedback from './TeamFeedback';
import { useUserInfo } from '../../../context/UserInfo';
import { routes } from '../../../routes';

import CandidateInfo from './CandidateInfo';
import './CandidateDetails.css';
import CommentsPopover from '../../../components/Comment/CommentsPopover';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { RelatedObjectNames } from '../../../components/Comment/types';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import { recruitmentRoutes } from '../../routes';
import { CandidateFlairAiCard } from './FlairAI/CandidateFlairAiCard';
import FunnelStagePath from './FunnelStagePath/FunnelStagePath';
import Communication from './Communication';
import { useUserInfoQuery } from '../../../__generated__/graphql';
import { useServerFeature } from '../../../../../hooks/useServerFeature';

type RouteParams = {
  candidateId: string;
  jobId?: string;
};

type Params = {
  candidate: JobCandidate;
};

type Props = {
  candidate: JobCandidate;
  children: React.ReactNode;
};

const i18Path = 'recruitment.candidate.detailsPage';

const Container: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  candidate,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const tRecruitmentNavigation = useNamespacedTranslation(
    'navigation.menuItems.recruitment',
  );
  const history = useHistory();

  const goBackButtonAction = () => {
    history.goBack();
  };

  useFlairBreadcrumbHook([
    { label: tRecruitmentNavigation('title') },
    {
      label: tRecruitmentNavigation('subMenuItems.jobs.title'),
      url: recruitmentRoutes.jobsList.route.create({}),
    },
    {
      label:
        candidate.job?.Name ?? tRecruitmentNavigation('unsolicitedApplication'),
      url: candidate.job
        ? recruitmentRoutes.jobCandidates.route.create({
            jobId: candidate.job.Id,
          })
        : '',
    },
    { label: candidate.flair__Full_Name__c },
  ]);

  return (
    <BootstrapContainer>
      <PageHeader
        goBackButtonAction={goBackButtonAction}
        title={t('title')}
        actions={
          <CommentsPopover
            recordId={candidate.Id}
            relatedObjectName={RelatedObjectNames.Candidate}
            mode="button"
            commentsCount={candidate.commentsCount}
          />
        }></PageHeader>
      <Row>
        <Col>
          <FunnelStagePath
            candidateId={candidate.Id}
            currentStage={candidate.flair__Funnel_Stage__c}
            stages={candidate.funnelStages}
          />
        </Col>
      </Row>
      {children}
    </BootstrapContainer>
  );
};

const Content: React.FC<React.PropsWithChildren<Params>> = ({ candidate }) => {
  const { id: myId } = useUserInfo();
  const communicationVisible = useCandidateCommunicationVisible(
    candidate.flair__Job__c,
  );

  if (!candidate.candidateEvaluations) {
    return <></>;
  }
  return (
    <Row>
      <Col xl={8}>
        <CandidateInfo candidate={candidate} />
        <MyFeedback
          candidateEvaluations={candidate.candidateEvaluations?.filter(
            (candidateEvaluation) =>
              candidateEvaluation.flair__Employee__c === myId,
          )}
        />

        <CandidateFlairAiCard candidate={candidate}></CandidateFlairAiCard>
        <TeamFeedback candidateEvaluations={candidate.candidateEvaluations} />
      </Col>
      <Col xl={4}>
        {communicationVisible && (
          <Communication
            key={candidate.Id}
            candidateId={candidate.Id}
            candidateEmail={candidate.flair__Email__c ?? null}
          />
        )}
        <Resume candidate={candidate} />
      </Col>
    </Row>
  );
};

const CandidateDetails = () => {
  const { candidateId } = useParams<RouteParams>();

  const { data, loading, error } = useCandidateDetailsQuery({
    variables: {
      id: candidateId,
    },
  });

  if (loading) {
    return <Loading />;
  }

  if (error || !data?.candidate) {
    return <Redirect to={routes.home.route.create({})} />;
  }

  return (
    // TODO
    <Container candidate={data.candidate as JobCandidate}>
      <Content candidate={data.candidate as JobCandidate} />
    </Container>
  );
};

export default CandidateDetails;

// Communication is restricted to hiring managers of the candidate's job;
// evaluators can open this page but must not see candidate emails. Admins can
// still hide it via the Candidate_Communication hub feature (kill switch).
const useCandidateCommunicationVisible = (
  candidateJobId: string | null | undefined,
): boolean => {
  const featureEnabled = useServerFeature('Candidate_Communication');
  const { data } = useUserInfoQuery();
  const hiringManagerJobIds =
    data?.hiringManager.jobs.map((job) => job.Id) ?? [];

  return (
    featureEnabled &&
    !!candidateJobId &&
    hiringManagerJobIds.includes(candidateJobId)
  );
};
